import {
  createGenericImpressionEvent,
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { PageTypeEnum } from '@moonpig/web-shared-products'
import {
  SearchStoreState,
  TrackingActionTypes,
  TrackingActionsEnum,
  TrackingPayload,
} from '../types'
import { getTrackingLabelForSortOrder } from '../../utils/getTrackingLabel'
import { createSearchGAEvent } from '../../analytics/createSearchGAEvent'
import { createViewGalleryResultsGAEvent } from '../../analytics/createViewGalleryResultsGAEvent'
import { QueryProductsProps } from '../slices/products'
import { trackLoadedProductsEvent } from './events'
import { generateDepartmentHeadersClickGAEvent } from '../../analytics/GAEvents'
import { ClearFiltersProps } from '../slices/filters'
import {
  getClearFiltersEventLabel,
  getNoResultsClearFiltersEventLabel,
} from '../helpers'
import { ApplyIntegerFilterProps } from '../slices/filters/types'
import { ApplicableFilter, IntegerFilter } from '../../services/types/services'
import { PageNavigationSource } from '../slices/products/types'

export const trackEvent = ({
  action,
  oldValues,
  newValues,
  payload,
}: {
  action: TrackingActionTypes
  payload: TrackingPayload
  oldValues: SearchStoreState
  newValues: SearchStoreState
}) => {
  switch (action) {
    case TrackingActionsEnum.toggleFiltersMenu:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: newValues.filtersMenuOpen
            ? 'filters opened'
            : 'filters closed',
          eventLabel: newValues.pageContext.title,
        }),
      )
      break

    case TrackingActionsEnum.toggleFiltersCategory:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction:
            newValues.selectedCategories.length >
            oldValues.selectedCategories.length
              ? 'expand'
              : 'collapse',
          eventLabel:
            newValues.selectedCategories.length >
            oldValues.selectedCategories.length
              ? newValues.selectedCategories[
                  newValues.selectedCategories.length - 1
                ].label
              : oldValues.selectedCategories[
                  oldValues.selectedCategories.length - 1
                ]?.label || /* istanbul ignore next */ '',
        }),
      )
      break

    case TrackingActionsEnum.clearFilters:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'clear filters',
          eventLabel: getClearFiltersEventLabel(payload as ClearFiltersProps[]),
        }),
      )

      break

    case TrackingActionsEnum.sortBy:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'sort by',
          eventLabel: getTrackingLabelForSortOrder(payload[0] as string),
        }),
      )
      break

    case TrackingActionsEnum.changeDepartment: {
      const searchTerm =
        newValues.searchContext.searchTerm ||
        newValues.searchContext.selectedSuggestion?.label ||
        ''
      const departmentName = payload[0] as DepartmentsEnum
      const departmentTitle = getDepartmentTitle(departmentName)
      const departmentCount =
        newValues.searchContext.departmentCounts.find(
          d => d.department === departmentName,
        )?.count || /* istanbul ignore next */ 0
      trackGAEvent(
        generateDepartmentHeadersClickGAEvent({
          searchTerm,
          departmentTitle,
          departmentCount,
          pageType: newValues.pageContext.type,
          pageTitle: newValues.pageContext.title,
        }),
      )

      break
    }

    case TrackingActionsEnum.queryProducts:
      if (!(payload[0] as QueryProductsProps).isLoadMore) {
        const queryProductsProps = payload[0] as QueryProductsProps
        const selectedFilters =
          queryProductsProps.filters ||
          /* istanbul ignore next */ newValues.selectedFilters
        const selectedIntegerFilter = queryProductsProps.integerFilter
          ? queryProductsProps.filteringEvent?.filter
          : undefined

        if (newValues.pageContext.type === PageTypeEnum.SEARCH) {
          trackGAEvent(
            createSearchGAEvent({
              correctedSearchTerm:
                newValues.searchContext.correctedSearchTerm || '',
              department: newValues.searchContext.departments,
              filters: [
                ...selectedFilters,
                ...(selectedIntegerFilter
                  ? [selectedIntegerFilter as IntegerFilter]
                  : []),
              ].map(f => ({
                key: f.id,
                group: f.parent,
              })),
              filteringEvent: newValues.filteringEvent,
              numberOfResults: newValues.results.totalCount,
              searchTerm: newValues.searchContext.searchTerm,
              sort: newValues.sortValue,
              selectedSuggestion: newValues.searchContext.selectedSuggestion,
              isTrendingSuggestion:
                !!newValues.searchContext.isTrendingSuggestion,
              showNbas: newValues.hasNbaFilters,
              showRudeProducts: newValues.hasRudeProducts,
              redirectedFrom: newValues.searchContext.redirectedFrom,
              redirectionSource: newValues.searchContext.redirectionSource,
            }),
          )

          trackLoadedProductsEvent({
            results: newValues.results,
            searchContext: newValues.searchContext,
            preAppliedFilters: newValues.preAppliedFilters,
            selectedFilters: newValues.selectedFilters,
            pageContext: newValues.pageContext,
            lastLoadedPage: newValues.lastLoadedPage,
            filteringEvent: newValues.filteringEvent,
            isPrevious: payload[0] as boolean,
            galleryPageSize: newValues.galleryPageSize,
          })
        }

        if (
          newValues.pageContext.type === PageTypeEnum.GALLERY ||
          newValues.pageContext.type === PageTypeEnum.COLLECTION
        ) {
          trackGAEvent(
            createViewGalleryResultsGAEvent({
              department: newValues.searchContext.departments,
              filters: [
                ...newValues.preAppliedFilters,
                ...[
                  ...selectedFilters,
                  ...(selectedIntegerFilter
                    ? [selectedIntegerFilter as IntegerFilter]
                    : []),
                ].map(f => ({
                  key: f.id,
                  group: f.parent,
                })),
              ],
              filteringEvent: newValues.filteringEvent,
              numberOfResults: newValues.results.totalCount,
              pageTitle: newValues.pageContext.title,
              sort: newValues.sortValue,
              showNbas: newValues.hasNbaFilters,
              showRudeProducts: newValues.hasRudeProducts,
              pageType: newValues.pageContext.type as PageTypeEnum,
            }),
          )

          trackLoadedProductsEvent({
            results: newValues.results,
            searchContext: newValues.searchContext,
            preAppliedFilters: newValues.preAppliedFilters,
            selectedFilters: newValues.selectedFilters,
            pageContext: newValues.pageContext,
            lastLoadedPage: newValues.lastLoadedPage,
            filteringEvent: newValues.filteringEvent,
            isPrevious: payload[0] as boolean,
            galleryPageSize: newValues.galleryPageSize,
          })
        }
      }
      break

    case TrackingActionsEnum.loadMoreProducts:
      trackLoadedProductsEvent({
        results: newValues.results,
        searchContext: newValues.searchContext,
        preAppliedFilters: newValues.preAppliedFilters,
        selectedFilters: newValues.selectedFilters,
        pageContext: newValues.pageContext,
        lastLoadedPage: newValues.lastLoadedPage,
        filteringEvent: newValues.filteringEvent,
        isPrevious: payload[0] as boolean,
        galleryPageSize: newValues.galleryPageSize,
      })
      break

    case TrackingActionsEnum.loadPage:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'navigate pages',
          eventLabel:
            payload[1] !== PageNavigationSource.Number
              ? `${payload[1]} page`
              : `go to page ${payload[0]}`,
        }),
      )
      break

    case TrackingActionsEnum.applyIntegerFilter:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'show milestone results',
          eventLabel:
            (
              payload[0] as ApplyIntegerFilterProps
            )?.filter?.selectedValue?.toString() ||
            (payload[0] as ApplyIntegerFilterProps)?.value?.toString() ||
            /* istanbul ignore next */ '',
        }),
      )
      if (newValues.noResultsFilters.length > 0) {
        trackGAEvent(
          createGenericImpressionEvent({
            category: `${newValues.pageContext.type} results`,
            action: 'zero results viewed',
            label: newValues.noResultsFilters.length.toString(),
          }),
        )
      }
      break

    case TrackingActionsEnum.clearFiltersWhenNoResults:
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'clear filters',
          eventLabel: getNoResultsClearFiltersEventLabel(
            payload as { filter: ApplicableFilter }[],
          ),
        }),
      )

      break
  }
}
